import {
  ExperimentDetails,
  ExperimentFeatureFlagKey,
  getExperimentFlagDetails,
} from '@reward-platform/devcycle'

import { isClient, isCypress, isProduction } from '~/utils/envChecks'
import { FeatureFlagValue } from '~/schemas/featureFlags'
import { useFeatureFlagContext } from './FeatureFlagContext'

type ExperimentFlag<T extends FeatureFlagValue = boolean> = ExperimentDetails & { value: T }

/**
 * Return an experiment flag object with details and the flag value,
 * or returns null when the experiment is not enabled
 * @param experimentFlagKey experiment flag key
 * */
const useExperimentFlag = <T extends FeatureFlagValue = boolean>(
  experimentFlagKey: ExperimentFeatureFlagKey
): ExperimentFlag<T> | null => {
  const { activeFeatureFlags } = useFeatureFlagContext()
  const featureDetails = activeFeatureFlags[experimentFlagKey]
  const experimentDetails = getExperimentFlagDetails(experimentFlagKey)

  if (!experimentDetails) {
    throw new Error(
      `No experiment matching flag key: ${experimentFlagKey}. Make sure the corresponding entry has been added to the EXPERIMENTS config`
    )
  }

  // To test a/b experiment flag in Storybook, add the key string to the window.activeExperiments array
  if (isClient() && window.activeExperiments && !isProduction()) {
    return window.activeExperiments.includes(experimentFlagKey)
      ? {
          ...experimentDetails,
          value: true as T,
        }
      : null
  }

  // ignore flags if running in Cypress
  if (isCypress()) {
    return null
  }

  return featureDetails ? { ...experimentDetails, value: featureDetails.value as T } : null
}

export default useExperimentFlag
